'use client';

import Card from '@tempcover/bolt-ui/card';
import { Icon } from '../Icon/Icon';
import { RadioButton } from '../RadioButton/RadioButton';
import { LinkButton } from '../LinkButton/LinkButton';
import { useState } from 'react';

interface PriceTable {
  value: {
    data: {
      hourlyCost: string;
      dailyCost: string;
      weeklyCost: string;
    };
  };
}

interface Props {
  heading: string;
  body: string;
  cardTitle: string;
  cardIcon: string;
  cardLegalText: string;
  CTAUrl: string;
  priceTable: PriceTable;
}

export const Calculator: React.FC<Props> = ({
  heading,
  body,
  cardTitle,
  cardIcon,
  cardLegalText,
  CTAUrl,
  priceTable,
}) => {
  const formatPrice = (price: string): string => {
    const value = parseFloat(price);
    return ((value * 100) / 100).toFixed(2);
  };

  const [price, setPrice] = useState(formatPrice(priceTable.value.data.dailyCost));

  return (
    <>
      <h2 className="mb-6">{heading}</h2>
      <p className="mb-6">{body}</p>
      <Card className="h-auto! max-w-[900px]">
        <div className="md:gap-regular flex flex-1 flex-col items-center md:flex-row">
          <div className="mb-4 md:mb-0 md:w-[50%]">
            <div className="gap-regular mb-6 flex w-full *:m-0 *:last:grow">
              <Icon name={cardIcon} aria-labelledby="calculator_title" />
              <h3 id="calculator_title" className="tc-heading-xs self-center">
                {cardTitle}
              </h3>
            </div>
            <fieldset className="mb-6 flex gap-2 md:gap-4">
              <legend className="mb-4 text-lg">Please select a duration below</legend>
              <RadioButton
                name="calculator_cost"
                label={'1 Hour'}
                checked={price === priceTable.value.data.hourlyCost}
                onClick={() => {
                  setPrice(formatPrice(priceTable.value.data.hourlyCost));
                }}
              />
              <RadioButton
                name="calculator_cost"
                label={'1 Day'}
                defaultChecked={true}
                checked={price === priceTable.value.data.dailyCost}
                onClick={() => {
                  setPrice(formatPrice(priceTable.value.data.dailyCost));
                }}
              />
              <RadioButton
                name="calculator_cost"
                label={'1 Week'}
                checked={price === priceTable.value.data.weeklyCost}
                onClick={() => {
                  setPrice(formatPrice(priceTable.value.data.weeklyCost));
                }}
              />
            </fieldset>
            <p>It couldn&apos;t be simpler to get a short term insurance policy.</p>
            <p className="mb-0 pb-0">
              You can buy a policy online anytime you need it, and with our straightforward quote process, you can get a
              price in under 2 minutes!
            </p>
          </div>
          <div className="bg-background-200 w-full rounded-xl p-4 inset-shadow-sm inset-shadow-neutral-100 md:w-[50%]">
            <h3 className="font-heading mt-0 mb-7 text-xl">The price for your selected duration is from:</h3>
            <p className="!font-title mb-9 text-5xl font-extrabold">just £{price}</p>
            <p className="mb-4 text-sm">{cardLegalText}</p>
            <LinkButton link={CTAUrl} label="Get your quote" variant="primary" size="large" />
          </div>
        </div>
      </Card>
    </>
  );
};
